import type { AdminUser } from '@nexo/types';
import { Avatar, Badge, IconButton, Input, Popover, useToast } from '@nexo/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ellipsis, Search } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import { useNavigate } from 'react-router';
import { UserTeamsDialog } from './AdminDetails';
import { useMe } from '../auth/AuthProvider';
import { AdminPage, DataTable, Pagination } from './AdminLayout';
import { errorMessage } from '../../lib/forms';

export function AdminUsers() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const query = useDeferredValue(q.trim());
  const users = useQuery({
    queryKey: ['admin', 'users', query, page],
    queryFn: () => api.admin.users({ q: query || undefined, page }),
    placeholderData: keepPreviousData,
  });

  return (
    <AdminPage
      title={t('Usuarios')}
      description={t('Cuentas de la plataforma: suspende, reactiva o gestiona administradores.')}
    >
      <SearchBox
        value={q}
        onChange={(v) => {
          setQ(v);
          setPage(1);
        }}
        placeholder={t('Buscar por nombre o email…')}
      />
      <DataTable
        head={[
          t('Usuario'),
          t('Estado'),
          t('Equipos'),
          t('Mensajes'),
          t('Última actividad'),
          t('Alta'),
          '',
        ]}
        empty={users.data?.items.length === 0}
      >
        {users.data?.items.map((user) => (
          <UserRow key={user.id} user={user} />
        ))}
      </DataTable>
      {users.data && (
        <Pagination
          page={users.data.page}
          pageSize={users.data.pageSize}
          total={users.data.total}
          onPage={setPage}
        />
      )}
    </AdminPage>
  );
}

function UserRow({ user }: { user: AdminUser }) {
  const { t } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const toast = useToast();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin'] });
  const update = useMutation({
    mutationFn: (input: { suspended?: boolean; isPlatformAdmin?: boolean }) =>
      api.admin.updateUser(user.id, input),
    onSuccess: () => void refresh(),
    onError: (err) =>
      toast({ title: t('No se pudo actualizar'), description: errorMessage(err), tone: 'error' }),
  });
  const remove = useMutation({
    mutationFn: () => api.admin.deleteUser(user.id),
    onSuccess: () => {
      toast({ title: t('Cuenta {{email}} eliminada', { email: user.email }), tone: 'success' });
      void refresh();
    },
    onError: (err) =>
      toast({ title: t('No se pudo eliminar'), description: errorMessage(err), tone: 'error' }),
  });
  const isMe = user.id === me.id;
  const [showTeams, setShowTeams] = useState(false);
  const navigate = useNavigate();

  return (
    <tr className="hover:bg-surface-2/50">
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-3">
          <Avatar id={user.id} name={user.displayName} src={user.avatarUrl} size="sm" />
          <div className="min-w-0">
            <p className="truncate font-medium">
              {user.displayName}{' '}
              {isMe && <span className="font-normal text-subtle">{t('(tú)')}</span>}
            </p>
            <p className="truncate text-xs text-muted">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex flex-wrap gap-1">
          {user.suspendedAt ? (
            <Badge tone="danger">{t('Suspendido')}</Badge>
          ) : (
            <Badge tone="success">{t('Activo')}</Badge>
          )}
          {user.isPlatformAdmin && <Badge tone="accent">Admin</Badge>}
        </div>
      </td>
      <td className="px-4 py-2.5 tabular-nums">
        {user.teamCount > 0 ? (
          <button
            type="button"
            onClick={() => setShowTeams(true)}
            title={t('Ver sus equipos')}
            className="rounded-md px-1.5 py-0.5 text-accent hover:bg-accent-soft hover:underline"
          >
            {user.teamCount}
          </button>
        ) : (
          <span className="px-1.5 text-muted">0</span>
        )}
        {showTeams && (
          <UserTeamsDialog
            user={user}
            onClose={() => setShowTeams(false)}
            onOpenTeam={(team) => void navigate(`/admin/teams/${team.id}`)}
          />
        )}
      </td>
      <td className="px-4 py-2.5 tabular-nums">{user.messageCount}</td>
      <td className="px-4 py-2.5 text-muted">
        {user.lastSeenAt ? formatRelative(user.lastSeenAt) : '—'}
      </td>
      <td className="px-4 py-2.5 text-muted">{formatRelative(user.createdAt)}</td>
      <td className="px-2 py-2.5 text-right">
        {!isMe && (
          <Popover
            className="w-56 p-1"
            trigger={({ toggle }) => (
              <IconButton
                label={t('Acciones para {{email}}', { email: user.email })}
                size="sm"
                onClick={toggle}
              >
                <Ellipsis />
              </IconButton>
            )}
          >
            {({ close }) => (
              <div role="menu">
                <MenuItem
                  label={user.suspendedAt ? t('Reactivar cuenta') : t('Suspender cuenta')}
                  onClick={() => {
                    close();
                    if (
                      user.suspendedAt ||
                      window.confirm(
                        t('¿Suspender a {{email}}? Perderá el acceso inmediatamente.', {
                          email: user.email,
                        }),
                      )
                    ) {
                      update.mutate({ suspended: !user.suspendedAt });
                    }
                  }}
                />
                <MenuItem
                  label={
                    user.isPlatformAdmin ? t('Quitar administrador') : t('Hacer administrador')
                  }
                  onClick={() => {
                    close();
                    update.mutate({ isPlatformAdmin: !user.isPlatformAdmin });
                  }}
                />
                <MenuItem
                  danger
                  label={t('Eliminar cuenta')}
                  onClick={() => {
                    close();
                    if (
                      window.confirm(
                        t(
                          '¿Eliminar definitivamente la cuenta {{email}} y sus mensajes? No se puede deshacer.',
                          { email: user.email },
                        ),
                      )
                    ) {
                      remove.mutate();
                    }
                  }}
                />
              </div>
            )}
          </Popover>
        )}
      </td>
    </tr>
  );
}

export function MenuItem({
  label,
  onClick,
  danger = false,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`flex w-full rounded-lg px-2.5 py-2 text-left text-sm hover:bg-surface-2 ${danger ? 'text-danger' : ''}`}
    >
      {label}
    </button>
  );
}

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <label className="relative mb-4 block max-w-sm">
      <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-subtle" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="pl-9"
      />
    </label>
  );
}
